import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, Check, ArrowRight, Search } from 'lucide-react';
import { Portal } from './Portal';

export interface SelectOption {
    value: string;
    label: string;
    icon?: React.ReactNode;
    description?: string;
    actionSlot?: React.ReactNode;
}

interface CustomSelectProps {
    value: string | string[];
    onChange: (value: any) => void;
    options: SelectOption[];
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    searchable?: boolean;
    isMulti?: boolean;
    showMultiActions?: boolean;
    error?: boolean;
    topSlot?: React.ReactNode;
    onSearchChange?: (query: string) => void;
    onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
    isLoading?: boolean;
    dropUp?: boolean;
    triggerClassName?: string;
    testId?: string;
}

export const CustomSelect = ({
    value,
    onChange,
    options,
    placeholder = 'Select an option',
    className = '',
    triggerClassName = '',
    disabled = false,
    searchable = false,
    isMulti = false,
    showMultiActions = false,
    error = false,
    topSlot,
    onSearchChange,
    onScroll,
    isLoading = false,
    dropUp = false,
    testId,
}: CustomSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [hoveredValue, setHoveredValue] = useState<string | null>(null);
    const [shouldDropUp, setShouldDropUp] = useState(false);
    const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const listRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const hasScrolledSelectedOnOpenRef = useRef(false);

    const selectedOptions = useMemo(() => {
        if (isMulti && Array.isArray(value)) {
            return options.filter(opt => value.includes(opt.value));
        }
        return options.filter(opt => opt.value === value);
    }, [options, value, isMulti]);

    const selectedOption = selectedOptions[0];

    const filteredOptions = useMemo(() => {
        if (!searchable || !searchQuery) return options;
        return options.filter(
            (opt) =>
                opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                opt.description?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [options, searchable, searchQuery]);

    const updatePosition = useCallback(() => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setTriggerRect(rect);

        if (!dropUp) {
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setShouldDropUp(spaceBelow < 300 && spaceAbove > spaceBelow);
        } else {
            setShouldDropUp(true);
        }
    }, [dropUp]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                // Also check if click was on the portal menu
                const menu = document.querySelector('[data-select-menu]');
                if (menu && menu.contains(e.target as Node)) return;
                setIsOpen(false);
            }
        };

        if (isOpen) {
            updatePosition();
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);

            if (searchable && searchInputRef.current) {
                // Focus after a short delay to allow portal to mount
                setTimeout(() => searchInputRef.current?.focus(), 10);
            }
        } else {
            setSearchQuery('');
            setHighlightedIndex(-1);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, searchable, updatePosition]);

    const handleSelect = (optionValue: string) => {
        if (isMulti) {
            const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
            const isSelected = currentValues.includes(optionValue);
            const nextValues = isSelected
                ? currentValues.filter(v => v !== optionValue)
                : [...currentValues, optionValue];
            onChange(nextValues);
        } else {
            onChange(optionValue);
            setIsOpen(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === "Enter" || e.key === "ArrowDown") {
                setIsOpen(true);
            }
            return;
        }

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev < filteredOptions.length - 1 ? prev + 1 : 0
            );
        }

        if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev > 0 ? prev - 1 : filteredOptions.length - 1
            );
        }

        if (e.key === "Enter") {
            e.preventDefault();
            if (highlightedIndex >= 0) {
                const option = filteredOptions[highlightedIndex];
                if (option) {
                    handleSelect(option.value);
                }
            }
        }

        if (e.key === "Escape") {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        const el = optionRefs.current[highlightedIndex];
        if (el) {
            el.scrollIntoView({
                block: "nearest",
            });
        }
    }, [highlightedIndex, isOpen]);

    useEffect(() => {
        if (!isOpen || isMulti || Array.isArray(value) || searchQuery) return;
        if (hasScrolledSelectedOnOpenRef.current) return;

        const selectedIndex = filteredOptions.findIndex((option) => option.value === value);
        if (selectedIndex < 0) return;

        hasScrolledSelectedOnOpenRef.current = true;
        window.setTimeout(() => {
            optionRefs.current[selectedIndex]?.scrollIntoView({
                block: "center",
            });
        }, 0);
    }, [filteredOptions, isMulti, isOpen, searchQuery, value]);

    useEffect(() => {
        if (!isOpen) {
            hasScrolledSelectedOnOpenRef.current = false;
        }
    }, [isOpen]);

    const getMenuStyle = (): React.CSSProperties => {
        if (!triggerRect) return { display: 'none' };

        return {
            position: 'fixed',
            top: shouldDropUp ? triggerRect.top - 8 : triggerRect.bottom + 8,
            left: triggerRect.left,
            width: triggerRect.width,
            maxWidth: Math.min(triggerRect.width, window.innerWidth - triggerRect.left - 16),
            zIndex: 1000, // Very high z-index for dropdowns
            pointerEvents: 'auto',
            transform: shouldDropUp ? 'translateY(-100%)' : 'none',
        };
    };

    return (
        <div ref={containerRef} className={`relative ${className}`} onKeyDown={handleKeyDown} tabIndex={0}>
            {/* Trigger Button */}
            <button
                type="button"
                data-testid={testId || "select-trigger-button"}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full px-4 py-2 border rounded-sm bg-white dark:bg-gray-800 text-left transition-all flex items-center justify-between ${disabled
                    ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : error
                        ? 'border-red-500 ring-4 ring-red-100 dark:ring-red-900/20'
                        : isOpen
                            ? 'border-gray-400 ring-4 ring-gray-100 dark:ring-gray-900/50'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    } ${triggerClassName}`}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {selectedOption?.icon && (
                        <div className="shrink-0 flex items-center justify-center text-xl min-w-[1.25rem]">
                            {selectedOption.icon}
                        </div>
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-300 truncate block">
                            {isMulti && Array.isArray(value) && value.length > 0 ? (
                                value.length === 1 ? selectedOptions[0]?.label : `${value.length} selected`
                            ) : (
                                selectedOption?.label || placeholder
                            )}
                        </span>
                    </div>
                </div>
                <ChevronDown
                    className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''
                        }`}
                />
            </button>

            {/* Dropdown Menu Portal */}
            {isOpen && (
                <Portal>
                    <div
                        data-select-menu
                        style={getMenuStyle()}
                        className={`bg-white dark:bg-gray-800 rounded-sm shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] border border-gray-100 dark:border-gray-700 overflow-hidden max-w-full animate-in fade-in duration-200 ${shouldDropUp
                            ? 'slide-in-from-bottom-2'
                            : 'slide-in-from-top-2'
                            }`}
                    >
                        {searchable && (
                            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            onSearchChange?.(e.target.value);
                                        }}
                                        placeholder="Search..."
                                        className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-none rounded-sm text-xs focus:ring-2 focus:ring-gray-200 outline-none text-gray-900 dark:text-gray-100"
                                    />
                                </div>
                            </div>
                        )}

                        {topSlot && (
                            <div className="border-b border-gray-100 dark:border-gray-700 p-2">
                                {topSlot}
                            </div>
                        )}

                        <div ref={listRef}
                            onScroll={onScroll}
                            onWheel={(event) => event.stopPropagation()}
                            onTouchMove={(event) => event.stopPropagation()}
                            className="max-h-64 overflow-y-auto overscroll-contain py-1 custom-scrollbar"
                            style={{ WebkitOverflowScrolling: 'touch' }}>
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option, index) => {
                                    const isHovered =
                                        hoveredValue === option.value ||
                                        filteredOptions[highlightedIndex]?.value === option.value;

                                    return (
                                        <button
                                            ref={(el) => {
                                                optionRefs.current[index] = el;
                                            }}
                                            key={option.value}
                                            type="button"
                                            onMouseEnter={() => setHoveredValue(option.value)}
                                            onMouseLeave={() => setHoveredValue(null)}
                                            onClick={() => handleSelect(option.value)}
                                            className={`group w-full px-3 py-1.5 text-left transition-all flex items-center gap-2 relative text-xs  ${(isMulti && Array.isArray(value) ? value.includes(option.value) : option.value === value)
                                                ? 'bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white font-semibold'
                                                : isHovered
                                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                                                }`}
                                        >
                                            {option.icon && (
                                                <div className="shrink-0 flex items-center justify-center text-sm min-w-[1rem] h-4 w-4">
                                                    {option.icon}
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0 overflow-hidden">
                                                <div className="truncate text-xs font-medium">{option.label}</div>
                                                {option.description && (
                                                    <div className={`text-[10px] leading-tight truncate ${(isMulti && Array.isArray(value) ? value.includes(option.value) : option.value === value) ? 'text-gray-500' : 'text-gray-400'}`}>
                                                        {option.description}
                                                    </div>
                                                )}
                                            </div>

                                            {option.actionSlot && (
                                                <div className="shrink-0 flex items-center justify-center ml-2">
                                                    {option.actionSlot}
                                                </div>
                                            )}

                                            {isHovered && !(isMulti && Array.isArray(value) ? value.includes(option.value) : option.value === value) && (
                                                <ArrowRight className="w-4 h-4 text-gray-400 animate-in fade-in slide-in-from-left-2 duration-200" />
                                            )}

                                            {(isMulti && Array.isArray(value) ? value.includes(option.value) : option.value === value) && (
                                                <Check className="w-4 h-4 text-green-500 shrink-0" />
                                            )}
                                        </button>
                                    );
                                })
                            ) : (
                                <div className="px-5 py-8 text-center text-sm text-gray-500">
                                    {isLoading ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                                            <span>Loading...</span>
                                        </div>
                                    ) : (
                                        'No results found'
                                    )}
                                </div>
                            )}
                            {isLoading && filteredOptions.length > 0 && (
                                <div className="px-5 py-3 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                                    <div className="w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                                    <span>Loading more...</span>
                                </div>
                            )}
                        </div>
                        {isMulti && showMultiActions && (
                            <div className="flex items-center justify-end gap-2 border-t border-gray-100 p-2 dark:border-gray-700">
                                <button
                                    type="button"
                                    onClick={() => onChange([])}
                                    className="rounded-sm px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="rounded-sm bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700"
                                >
                                    Done
                                </button>
                            </div>
                        )}
                    </div>
                </Portal>
            )}
        </div>
    );
};
